import React, { useEffect, useState } from 'react';

import { GridRenderCellParams } from '@mui/x-data-grid';

import DetailsModal, { Fields, Values } from '../../components/DetailsModal';
import PageContent from '../../components/PageContent';
import StaffGroupChipsArray from '../../components/StaffGroupChipsArray';
import UserDisabledStateChip from '../../components/UserDisabledStateChip';
import UsersList from '../../components/UsersList';
import { useGetGroupsQuery } from '../../services/permissions';
import {
  useGetStaffsQuery, useNewStaffMutation,
} from '../../services/staff';
import { ShortStaff } from '../../services/staff/types';
import { UserGroup } from '../../types/common';
import useSearchStr from '../../utils/react/hooks/useSearchStr';
import useSuccess from '../../utils/react/hooks/useSuccess';

import StaffDetails from './StaffDetails';

const INITIAL_NEW_FORM = {
  group: 'Teachers',
  email: '',
  phoneNumber: '+61',
  firstName: '',
  lastName: '',
  permissionGroups: [],
};

const NEW_FORM_FIELDS: Fields = {
  group: {
    label: 'Primary Group',
    editable: true,
    type: 'select',
    typeOptions: [{
      label: 'Teachers',
      value: UserGroup.Teachers,
    }, {
      label: 'Admins',
      value: UserGroup.Admins,
    }],
  },
  email: {
    editable: true,
    type: 'text',
  },
  phoneNumber: {
    editable: true,
    type: 'text',
  },
  firstName: {
    editable: true,
    type: 'text',
  },
  lastName: {
    editable: true,
    type: 'text',
  },
  permissionGroups: {
    editable: true,
    type: 'array',
    typeOptions: [],
  },
};

const AdminStaffsPage = () => {
  /**
   * Staff Queries & Mutations
   */
  const [searchStr, setSearchStr] = useSearchStr('');
  const { data: staff,
    isLoading: isStaffLoading } = useGetStaffsQuery({
    ...(searchStr ? { search: searchStr } : {}),
  });

  /**
   * Forms
   */
  const [staffDetails, setStaffDetails] = useState<ShortStaff>();
  const [newStaffFormOpen, setNewStaffFormOpen] = useState<boolean>(false);

  /**
   * User Groups
   */
  // Fetch permissionGroups when an entry form is open
  const { data: permissionGroupOpts } = useGetGroupsQuery({}, {
    skip: !newStaffFormOpen,
  });

  /**
   * Entry Form
   */
  const [newStaffForm, setNewStaffForm] = useState({});
  const [newStaffFormFields, setNewStaffFormFields] = useState<Fields>({});
  const [newStaff,
    {
      isSuccess: isNewStaffSuccess,
      isLoading: isNewStaffLoading,
      error: newStaffError,
      reset: resetNewStaff,
    }] = useNewStaffMutation();

  const resetNewStaffFormFields = () => {
    setNewStaffForm(INITIAL_NEW_FORM);
    setNewStaffFormFields({
      ...NEW_FORM_FIELDS,
      permissionGroups: {
        editable: true,
        type: 'array',
        typeOptions: permissionGroupOpts?.data.map((x) => ({
          label: x.name,
          value: x.groupId,
        })) || [],
      },
    });
  };

  useEffect(() => {
    // Update the new staff form when the permission groups are fetched
    resetNewStaffFormFields();
  }, [permissionGroupOpts]);

  useEffect(() => {
    // Close new staff form on success
    if (isNewStaffSuccess) setNewStaffFormOpen(false);
  }, [isNewStaffSuccess]);

  useSuccess(isNewStaffSuccess, 'Staff created');

  const resetMutationStates = async () => {
    resetNewStaff();
    resetNewStaffFormFields();
  };

  /**
   * Entry form functions
   */

  const onNewStaffCreate = (values?: Values) => {
    if (values) {
      newStaff({
        attributes: {
          email: values.email,
          phoneNumber: values.phoneNumber,
          firstName: values.firstName,
          lastName: values.lastName,
          gradYear: values.gradYear,
          group: values.group,
          permissionGroups: values.permissionGroups || [],
        },
      });
    }
  };

  /**
   * Render functions
   */

  const renderDisabledState = (params: GridRenderCellParams<any, boolean>) =>
    (<UserDisabledStateChip size="small" disabled={params?.value || false} />);

  const renderGroups = (params: GridRenderCellParams<any, UserGroup[]>) => (
    <StaffGroupChipsArray groups={params.value || []} limit={2} chipProps={{ size: 'small' }} />
  );

  return (
    <PageContent
      sideBar={staffDetails ? (
        <StaffDetails
          staff={staffDetails}
          onClose={() => setStaffDetails(undefined)}
        />
      ) : <div />}
      sideBarOpen={!!staffDetails}
    >
      <UsersList
        columns={[{
          field: 'groups',
          headerName: 'Groups',
          width: 200,
          renderCell: renderGroups,
        }, {
          field: 'name',
          headerName: 'Name',
          width: 200,
        }, {
          field: 'disabled',
          headerName: 'State',
          width: 200,
          renderCell: renderDisabledState,
        }]}
        onSearch={(s) => setSearchStr(s)}
        selectedRowId={staffDetails?.id}
        data={staff?.data || []}
        loading={isStaffLoading}
        onAddClick={() => {
          resetMutationStates();
          setNewStaffFormOpen(true);
        }}
        onRowClick={(entry: any) => setStaffDetails(entry.row)}
        addButtonLabel="Add Staff"
      />
      <DetailsModal
        open={newStaffFormOpen}
        handleClose={() => setNewStaffFormOpen(false)}
        title="Add New Staff"
        fields={newStaffFormFields}
        values={newStaffForm}
        error={newStaffError}
        buttons={[{
          fn: async (fields?: Fields) =>
            onNewStaffCreate(fields),
          label: 'Add',
          color: 'darkPrimary',
          loading: isNewStaffLoading,
        }]}
        onFieldChange={(key, value) => {
          setNewStaffForm({
            ...newStaffForm,
            [key]: value,
          });
        }}
      />
    </PageContent>
  );
};
export default AdminStaffsPage;
